import React, { useEffect, useState } from "react";

// Define an interface for circle properties
interface Circle {
  id: number;
  size: number;
  top: number;
  left: number;
  animationDelay: number;
  opacity: number;
}

const CirclesLoader: React.FC = () => {
  const [circles, setCircles] = useState<Circle[]>([]);

  useEffect(() => {
    // Function to check if a new circle overlaps with existing circles
    const isOverlapping = (
      newCircle: Circle,
      existingCircles: Circle[]
    ): boolean => {
      for (const circle of existingCircles) {
        // Calculate distance between circle centers
        const dx: number = newCircle.left - circle.left;
        const dy: number = newCircle.top - circle.top;
        const distance: number = Math.sqrt(dx * dx + dy * dy);

        // If distance is less than sum of radii, circles overlap
        if (distance < newCircle.size / 2 + circle.size / 2) {
          return true;
        }
      }
      return false;
    };

    // Generate non-overlapping circles
    const circleCount: number = 20; // Reduced count to make placement easier
    const containerWidth: number = 300;
    const containerHeight: number = 300;
    const newCircles: Circle[] = [];
    let attempts: number = 0;
    const maxAttempts: number = 100; // Prevent infinite loops

    while (newCircles.length < circleCount && attempts < maxAttempts) {
      attempts++;

      // Generate random properties
      const size: number = Math.floor(Math.random() * 60) + 20; // Random size between 20px and 80px
      const radius: number = size / 2;

      // Ensure circles stay within container bounds
      const top: number =
        Math.floor(Math.random() * (containerHeight - size)) + radius;
      const left: number =
        Math.floor(Math.random() * (containerWidth - size)) + radius;

      const newCircle: Circle = {
        id: newCircles.length,
        size,
        top,
        left,
        animationDelay: Math.random() * 2,
        opacity: Math.random() * 0.5 + 0.2,
      };

      // Add circle if it doesn't overlap with existing circles
      if (!isOverlapping(newCircle, newCircles)) {
        newCircles.push(newCircle);
        attempts = 0; // Reset attempts counter after successful placement
      }
    }

    setCircles(newCircles);
  }, []);

  return (
    <div className='w-[300px] h-[300px] relative mx-auto'>
      {circles.map((circle: Circle) => (
        <div
          key={circle.id}
          className='absolute rounded-full bg-gray-300 animate-pulse'
          style={{
            width: `${circle.size}px`,
            height: `${circle.size}px`,
            top: `${circle.top - circle.size / 2}px`, // Adjust for center positioning
            left: `${circle.left - circle.size / 2}px`, // Adjust for center positioning
            animationDelay: `${circle.animationDelay}s`,
            opacity: circle.opacity,
            transition: "all 0.5s ease-in-out",
          }}
        />
      ))}
    </div>
  );
};

export default CirclesLoader;
